import type { UIProductCardBadge } from 'blocks/product-card';
import { ProductDocument } from '@util/types/firestore/products';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';
import { getProductUrl } from '@util/urlHelpers';
import { getBunnyOptimizedSrc } from '@util/images/bunnyOptimizer';

// Product cards render the thumbnail through UIProductCard's raw <img>, which
// does NOT go through SafeImage, so Bunny optimizer params were never applied
// (web#1795). next.config.js sets images.unoptimized, so appending the Bunny
// params here is the only optimization these card images get. 600px covers a
// square card at 2x DPR on both mobile (2-col) and desktop grids; Bunny won't
// upscale past the source. No-op for non-b-cdn hosts, so it's safe for any
// thumbnail URL.
const CARD_THUMBNAIL_WIDTH = 600;

// ProductDocument -> UIProductCard props. Kept free of UI imports so it stays
// unit-testable (jest can't transform lucide-react's ESM through a component).

// The US destinations a domestic buyer can hit (orderValidator resolves AK/HI/PR
// to their own code, everything else in the US to 'US'). Alaska, Hawaii and
// Puerto Rico are the US — a listing that ships free to the lower 48 but charges
// for them is NOT free shipping.
const US_SHIPPING_CODES = ['US', 'AK', 'HI', 'PR'];

/**
 * `shipping_costs` is the seller's own flat-rate table, and checkout only bills
 * from it when `is_flat_rate` is true. On a carrier-rate listing checkout calls
 * ShipStation (`useRates`, which skips the API only for flat-rate products), so
 * whatever sits in `shipping_costs` is not the price the buyer will pay — no
 * shipping claim on the card can be honoured. Gate every such claim on this.
 */
export function hasQuotableShipping(item: ProductDocument): boolean {
    return item.is_flat_rate === true && !!item.shipping_costs?.length;
}

/**
 * Free shipping to every US destination the listing serves — not just "some
 * option is $0". Requires a flat-rate listing (see hasQuotableShipping), a free
 * continental-US ('US') option, and that every US-destination option present
 * (AK/HI/PR included) is also free. This is stricter than the legacy
 * `.some(cost === 0)`, which lit the badge for listings that paired free
 * lower-48 shipping with $25–50 AK/HI charges.
 */
export function hasFreeShipping(item: ProductDocument): boolean {
    if (!hasQuotableShipping(item)) return false;
    const costs = item.shipping_costs;
    if (!costs?.length) return false;
    const base = costs.find((o) => o.code === 'US');
    if (!base || base.cost !== 0) return false;
    return costs
        .filter((o) => US_SHIPPING_CODES.includes(o.code))
        .every((o) => o.cost === 0);
}

/**
 * Listing synced from a vetted external partner rather than a GearFocus seller.
 * Mirrors the legacy card: an integration source or external_source set to
 * anything other than our own 'refocus'.
 *
 * This path was raised in the Rev 2 homepage review as a possible conflict with
 * the site-wide "Verified sellers only" / "sellers we verify one by one" claims,
 * on the theory that partner-synced inventory might bypass seller verification.
 * It does not: partners are verified too (Kyle, Aug 2026), so the claims hold as
 * written and the copy stays. Recorded here rather than in a review document
 * because this function is where the question surfaces.
 */
export function isTrustedPartner(item: ProductDocument): boolean {
    const integrationSource = item.integrations?.source;
    if (integrationSource && integrationSource !== 'refocus') return true;
    return !!item.external_source && item.external_source !== 'refocus';
}

/** Discount off the seller's original price, 0 when not on sale. */
export function getSalePercent(item: ProductDocument): number {
    if (!item.on_sale || !item.og_price) return 0;
    return Math.round(((item.og_price - item.price) / item.og_price) * 100);
}

/** Legacy `useProductCard` treats absent/zero stock as sold out. */
export function isSoldOut(item: ProductDocument): boolean {
    return !!item.out_of_stock || !(item.stock > 0);
}

/**
 * Minimum discount worth calling a deal. Governs BOTH the deal badge and the
 * struck-through original price with its "-N% OFF" chip.
 *
 * These used to disagree: the badge required >= 5% but originalPrice was passed
 * for any positive discount, and UIProductCard renders its percentage chip
 * purely from originalPrice — so a 1% markdown produced a "-1% OFF" chip with
 * no badge, advertising a discount too small to be worth the pixels.
 */
const DEAL_MIN_PERCENT = 5;

export function toProductCardProps(
    item: ProductDocument,
    storeSale?: { unitCents: number; applied: { percentOff: number } | null }
) {
    const salePercent = getSalePercent(item);
    const storeSaleApplied = !!storeSale?.applied;
    const isDeal =
        !!item.on_sale && item.og_price > item.price && salePercent >= DEAL_MIN_PERCENT;

    return {
        title: item.title,
        price: storeSaleApplied ? storeSale!.unitCents / 100 : item.price,
        originalPrice: storeSaleApplied ? item.price : isDeal ? item.og_price : undefined,
        brand: item.brand,
        condition: getConditionShortLabel(item.condition),
        image: item.thumbnail
            ? {
                  src: getBunnyOptimizedSrc(item.thumbnail, CARD_THUMBNAIL_WIDTH),
                  alt: item.title,
              }
            : undefined,
        href: getProductUrl(item.slug),
        watching: item.favorite_count,
        freeShipping: hasFreeShipping(item),
        trustedPartner: isTrustedPartner(item),
        // Never badge a listing nobody can buy. The sold-out veil and the
        // disabled button already say so; a "DEAL" flash next to them reads as
        // a live offer.
        badges:
            (storeSaleApplied || isDeal) && !isSoldOut(item) ? (['deal'] as UIProductCardBadge[]) : undefined,
    };
}
